/**
 * Manual flight control — the device half.
 *
 * `manual-vector.ts` answers "what does this gesture mean". This module answers
 * the questions that come BEFORE the gesture: what kind of pointer is driving
 * the surface, whether the browser can run the control at all, which corner the
 * operator's thumb reaches, and where the screen-wake and battery facts stand.
 * Pure functions over plain values — no React, no DOM lookups of their own —
 * so every rule below is testable on a bare node process, exactly like the
 * geometry is.
 *
 * Three rules shape all of it:
 *
 * 1. **Feature-detect, never sniff.** The target is "any browser on a tablet",
 *    not one vendor, so nothing here reads a user-agent string. A capability is
 *    tested for; a device class is measured from the viewport and the pointer
 *    media query. A vendor that ships something new tomorrow is covered without
 *    an allowlist edit.
 * 2. **Degrade VISIBLY.** Every optional capability that is missing produces a
 *    sentence the operator can read, not a silently reduced feature. The only
 *    thing that fails silently is the Battery Status API, because a warning
 *    that cannot be produced is not a state — see `MANUAL_BATTERY_WARN_LEVEL`.
 * 3. **Core capabilities are not degradable.** Pointer events, `touch-action`
 *    and `setPointerCapture` are the control. Without them there is nothing to
 *    degrade to, so the MANUAL key is refused with the missing names in it.
 */
import type { PointerMode, SurfaceSize } from './manual-vector';

// ---------------------------------------------------------------------------
// Device class
// ---------------------------------------------------------------------------

/**
 * Manual flight is offered on a tablet or larger. A phone is refused outright:
 * the contract's minimum control surface is 280 px on touch and the STOP key is
 * 120×44 px, and a phone in landscape has no room for both plus a map worth
 * looking at.
 */
export type ManualDeviceClass = 'desktop' | 'tablet' | 'phone';

/**
 * Shorter viewport side, in CSS px, below which a coarse-pointer device is a
 * phone rather than a tablet.
 *
 * 600 is Android's own `sw600dp` tablet breakpoint and it lands cleanly between
 * the two populations: the largest phones (iPhone 16 Pro Max is 440×956 CSS px)
 * sit well below it, the smallest tablets (iPad mini, 744×1133) well above.
 * Measured rather than sniffed, so a device nobody has heard of is classified by
 * what it actually is.
 */
export const MANUAL_TABLET_MIN_SHORT_SIDE_PX = 600;

/** What the surrounding hook measured. A snapshot, so the rules below stay pure. */
export interface ManualViewport extends SurfaceSize {
  /** `matchMedia('(pointer: coarse)')` — a finger, not a mouse. */
  coarsePointer: boolean;
}

/**
 * Which pointer is driving the surface.
 *
 * Not a screen-size guess: `manual-vector.ts` sizes the grab halo, the ring and
 * the positional uncertainty from this, and a mouse on a 1024 px window needs
 * the desktop numbers while a finger on the same pixels needs the touch ones.
 */
export function pointerModeFor(viewport: ManualViewport): PointerMode {
  return viewport.coarsePointer ? 'touch' : 'desktop';
}

/**
 * Desktop, tablet or phone — from the pointer and the shorter viewport side.
 *
 * A fine pointer is a desktop whatever the window size: a 500 px browser window
 * on a laptop is a small surface, which `ringGeometry` already refuses on its
 * own terms, not a phone to be told to find a tablet.
 */
export function deviceClassFor(viewport: ManualViewport): ManualDeviceClass {
  if (!viewport.coarsePointer) return 'desktop';
  const shortSidePx = Math.min(
    Math.max(0, viewport.widthPx),
    Math.max(0, viewport.heightPx),
  );
  return shortSidePx < MANUAL_TABLET_MIN_SHORT_SIDE_PX ? 'phone' : 'tablet';
}

/**
 * True when the device is taller than it is wide.
 *
 * There is no portrait control layout, deliberately: the surface needs a square
 * of map for the ring plus two reachable bottom corners, and a portrait tablet
 * gives a tall strip. The operator is asked to rotate rather than handed a
 * cramped variant of a flight control.
 */
export function isPortrait(viewport: SurfaceSize): boolean {
  return viewport.heightPx > viewport.widthPx;
}

/**
 * Why manual flight is refused on a surface taller than it is wide, in words the
 * operator can act on.
 *
 * `isPortrait` is a pure aspect-ratio test, so it is true of a portrait-mounted
 * ops-room monitor and of a narrow desktop window just as much as of an upright
 * tablet — and those operators cannot "turn the tablet". A fine pointer is
 * therefore told what it can actually do about a window, and only a coarse
 * pointer is asked to rotate the device.
 *
 * This is a REFUSAL string, fed to `manualControlAvailability` before authority
 * is requested. The surface's own `MANUAL_TOUCH_NOTICE.rotate*` copy stays what
 * it is: a prompt shown to somebody who is already holding the aircraft and has
 * rotated mid-session.
 */
export function manualPortraitRefusal(pointer: PointerMode): string {
  return pointer === 'touch'
    ? 'Rotate to landscape — there is no portrait layout for manual flight.'
    : 'Manual flight needs a landscape window — this one is taller than it is wide.';
}

// ---------------------------------------------------------------------------
// Capabilities
// ---------------------------------------------------------------------------

/**
 * What this browser can actually do. Every field is a runtime test, and every
 * one of them has a named consequence in the UI when it is false.
 */
export interface ManualCapabilities {
  /** CORE. Without `PointerEvent` there is no unified mouse/touch/pen gesture. */
  pointerEvents: boolean;
  /** CORE. Without `touch-action: none` the browser pans the page under the drag. */
  touchAction: boolean;
  /** CORE. Without capture a finger that slips off the halo silently ends the drag. */
  pointerCapture: boolean;
  /** Optional. Absent → `SCREEN MAY SLEEP` in the banner. */
  wakeLock: boolean;
  /** Optional and never load-bearing — Safari has no `navigator.vibrate`. */
  vibrate: boolean;
  /** Optional. Absent → no battery warning at all, silently. */
  battery: boolean;
  /** All three core capabilities are present. */
  core: boolean;
}

/** Everything present — the assumption the server renders with, and the test default. */
export const FULL_MANUAL_CAPABILITIES: ManualCapabilities = {
  pointerEvents: true,
  touchAction: true,
  pointerCapture: true,
  wakeLock: true,
  vibrate: true,
  battery: true,
  core: true,
};

/**
 * Probe the current browser.
 *
 * `touch-action` is asked for two ways and either answer is enough: `CSS.supports`,
 * which is the direct question, and the presence of the property on a style
 * declaration, which is what the Pointer Events polyfills have always used. Both
 * are cheap, and a false NEGATIVE here disables manual flight on a browser that
 * would have run it — the one failure mode worth spending a second check on.
 */
export function detectManualCapabilities(
  view: (Window & typeof globalThis) | null | undefined = globalThis.window,
): ManualCapabilities {
  if (!view) return FULL_MANUAL_CAPABILITIES;

  const pointerEvents = typeof view.PointerEvent === 'function';
  const touchAction =
    (typeof view.CSS?.supports === 'function' && view.CSS.supports('touch-action', 'none'))
    || 'touchAction' in (view.document?.documentElement?.style ?? {});
  const pointerCapture =
    typeof view.Element?.prototype?.setPointerCapture === 'function';

  const nav = view.navigator as
    | (Navigator & {
        wakeLock?: unknown;
        vibrate?: unknown;
        getBattery?: unknown;
      })
    | undefined;

  return {
    pointerEvents,
    touchAction,
    pointerCapture,
    wakeLock: typeof nav?.wakeLock === 'object' && nav.wakeLock !== null,
    vibrate: typeof nav?.vibrate === 'function',
    battery: typeof nav?.getBattery === 'function',
    core: pointerEvents && touchAction && pointerCapture,
  };
}

/** Operator-facing names for the three core capabilities, in refusal order. */
const CORE_CAPABILITY_LABEL: readonly [keyof ManualCapabilities, string][] = [
  ['pointerEvents', 'pointer events'],
  ['touchAction', 'touch-action'],
  ['pointerCapture', 'pointer capture'],
];

/**
 * Why this browser cannot fly, or null when it can.
 *
 * Names what is missing rather than saying "unsupported browser": an operator
 * who reads "pointer capture" can act on it (a different browser, an updated
 * one), where "unsupported" leaves them guessing whether the aircraft is at
 * fault. Same discipline as every other string in `MANUAL_REFUSAL`.
 */
export function manualInputRefusal(capabilities: ManualCapabilities): string | null {
  const missing = CORE_CAPABILITY_LABEL.filter(([key]) => !capabilities[key]).map(([, label]) => label);
  if (missing.length === 0) return null;
  return `This browser cannot run manual flight — no ${missing.join(', ')}.`;
}

/**
 * The one refusal that is about the device rather than the browser or the
 * aircraft. Phones are out; `MANUAL_REFUSAL.viewportTooNarrow` is the contract's
 * sentence for it, and it is deliberately the same one the surface shows when a
 * desktop window is too small — the operator's answer is the same either way.
 */
export const MANUAL_TOUCH_NOTICE = {
  /** Shown INSTEAD of the control while a tablet is held upright. */
  rotateTitle: 'Rotate to landscape',
  rotateDetail: 'There is no portrait layout for manual flight — turn the tablet and the control returns.',
  /** Persistent banner note when the screen wake lock was refused or is absent. */
  wakeLockRefused: 'SCREEN MAY SLEEP',
  wakeLockDetail: 'The screen wake lock was refused — set the display timeout by hand before flying.',
} as const;

/** `TABLET BATTERY 12% — charge before flying.` */
export function manualBatteryWarning(level: number): string {
  return `Tablet battery ${Math.max(0, Math.round(level * 100))}% — charge before flying.`;
}

/**
 * Below this the banner warns. 15% is the contract's figure; the warning is
 * skipped entirely, with no placeholder, where the Battery Status API does not
 * exist — a battery readout nobody can measure is not something to invent.
 */
export const MANUAL_BATTERY_WARN_LEVEL = 0.15;

/**
 * A haptic pulse, if this browser has one.
 *
 * Returns whether it fired, so a caller can be tested for "never load-bearing":
 * every event that vibrates must also produce something visible, and Safari —
 * which has no `navigator.vibrate` at all — must lose nothing but the buzz.
 */
export function vibrate(
  pattern: number | readonly number[],
  view: Window | undefined = globalThis.window,
): boolean {
  const nav = view?.navigator as (Navigator & { vibrate?: (p: number | number[]) => boolean }) | undefined;
  if (typeof nav?.vibrate !== 'function') return false;
  try {
    return nav.vibrate(typeof pattern === 'number' ? pattern : [...pattern]) !== false;
  } catch {
    // A vibration can be refused outright (a user-activation policy, a muted
    // device). It is a nicety; nothing above it may branch on the failure.
    return false;
  }
}

/** Haptic patterns, ms. Short — this is confirmation, not an alarm. */
export const MANUAL_HAPTIC = {
  /** A target pin dropped. Visual cue: the pin and the confirm bar appear. */
  pinDropped: 15,
  /** STOP. Visual cue: the vector disappears and the readout reads HOVER. */
  stopped: [30, 40, 30],
} as const;

// ---------------------------------------------------------------------------
// Handedness
// ---------------------------------------------------------------------------

/**
 * Which hand holds the control corner.
 *
 * A tablet is held two-handed, so the CENTRE of the screen is the least
 * reachable place on it — the opposite of a desktop, where the centre is where
 * the cursor already is. The control surface therefore sits in one bottom
 * corner and STOP in the other, and which is which is the operator's choice,
 * remembered like the stage layout is.
 */
export type ManualHandedness = 'L' | 'R';

export const MANUAL_HANDEDNESS: readonly ManualHandedness[] = ['L', 'R'];

/** Right-handed by default, which is where the population is. */
export const DEFAULT_MANUAL_HANDEDNESS: ManualHandedness = 'R';

export const MANUAL_HANDEDNESS_LABEL: Record<ManualHandedness, string> = {
  L: 'LEFT',
  R: 'RIGHT',
};

export const MANUAL_HANDEDNESS_DESCRIPTION: Record<ManualHandedness, string> = {
  L: 'Control in the bottom-left corner, STOP on the right',
  R: 'Control in the bottom-right corner, STOP on the left',
};

const HANDEDNESS_STORAGE_PREFIX = 'manual_flight_handed';

/** Per operator, for the same reason the layout key is: a shared console is real. */
export function manualHandednessStorageKey(operator?: string | null): string {
  const who = operator?.trim();
  return who ? `${HANDEDNESS_STORAGE_PREFIX}:${who}` : HANDEDNESS_STORAGE_PREFIX;
}

export function isManualHandedness(value: unknown): value is ManualHandedness {
  return value === 'L' || value === 'R';
}

/** Never throws — `localStorage` can be unavailable, and a preference is not a gate. */
export function readManualHandedness(operator?: string | null): ManualHandedness {
  if (typeof window === 'undefined') return DEFAULT_MANUAL_HANDEDNESS;
  try {
    const stored = window.localStorage.getItem(manualHandednessStorageKey(operator));
    return isManualHandedness(stored) ? stored : DEFAULT_MANUAL_HANDEDNESS;
  } catch {
    return DEFAULT_MANUAL_HANDEDNESS;
  }
}

/** False when the choice could not be persisted, so the caller can say so. */
export function writeManualHandedness(handed: ManualHandedness, operator?: string | null): boolean {
  if (typeof window === 'undefined') return false;
  try {
    window.localStorage.setItem(manualHandednessStorageKey(operator), handed);
    return true;
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// Thumb zones
// ---------------------------------------------------------------------------

export interface ManualControlAnchor {
  /** Where the aircraft symbol and the ring centre sit, in surface px. */
  xPx: number;
  yPx: number;
  /**
   * Displacement of that point from the surface centre, px. This is what the
   * map is asked to hold the aircraft at, so the grab halo stays ON the symbol
   * once the control leaves the middle of the screen.
   */
  offsetXPx: number;
  offsetYPx: number;
  /** Which bottom corner STOP takes — always the one the control did not. */
  stopCorner: 'left' | 'right';
  /** True when the anchor had to fall back to the centre for want of room. */
  centred: boolean;
}

/**
 * Where the control sits on this surface.
 *
 * Desktop keeps the centre: a mouse reaches everywhere equally, and the centre
 * is where a map already holds the thing being flown. Touch moves it into the
 * handed bottom corner, far enough in that the whole outer ring — plus the yaw
 * ring, which lives outside it — stays on the surface.
 *
 * `marginPx` is the caller's outermost drawn radius, because the yaw ring's gap
 * and stroke are the control surface's own drawing decisions and do not belong
 * in a geometry rule. When the surface is too small to hold the ring anywhere
 * but the middle, the anchor falls back to the centre and says so rather than
 * pushing the ring off the edge: a control the operator cannot see all of is
 * worse than one they have to stretch for.
 */
export function manualControlAnchor(
  surface: SurfaceSize,
  options: { pointer: PointerMode; handed: ManualHandedness; marginPx: number },
): ManualControlAnchor {
  const widthPx = Math.max(0, surface.widthPx);
  const heightPx = Math.max(0, surface.heightPx);
  const centreX = widthPx / 2;
  const centreY = heightPx / 2;
  const stopCorner = options.handed === 'R' ? 'left' : 'right';

  const centredAnchor: ManualControlAnchor = {
    xPx: centreX,
    yPx: centreY,
    offsetXPx: 0,
    offsetYPx: 0,
    stopCorner,
    centred: true,
  };
  if (options.pointer !== 'touch') return centredAnchor;

  const marginPx = Math.max(0, options.marginPx);
  // Room for the ring at BOTH extremes, or there is no corner to move to.
  if (widthPx < marginPx * 2 || heightPx < marginPx * 2) return centredAnchor;

  const xPx = options.handed === 'R' ? widthPx - marginPx : marginPx;
  const yPx = heightPx - marginPx;
  return {
    xPx,
    yPx,
    offsetXPx: xPx - centreX,
    offsetYPx: yPx - centreY,
    stopCorner,
    centred: false,
  };
}
